// The sub-action pop-up (kanban 2a5883db, owner 2026-10-12: clicking a
// sub-action "brings up the whole entire list of everything; there should be
// a zoomed-in one scoped to just this individual thing"). One action of one
// initiative: its status, who holds it, its dates, what it waits on and what
// waits on it, Merlin's note, the quotes behind it, and one link to the whole
// initiative. Same shell as InitiativeModal (backdrop, Escape, focus) so the
// two pop-ups feel like one thing.

import { useEffect, useMemo, useRef } from 'react';
import type { InitiativeAction, InitiativeState } from '../../services/roadmapReports';
import { ACTION_STATUS_LABELS, actionBlockerRefs, actionDateSpan, actionHoldsUpRefs, actionIsLate, liveActions, STATUS_LABELS, type ActionRef, type TeamColors, accentTeamOf } from './roadmapViewUtils';
import { fmtDayYear, parseDay, type EvidenceSource } from './roadmapTimelineUtils';
import { EvidenceList, type OpenSourceFn } from './EvidenceList';
import { CitedSources } from './CitedSources';
import { evidenceForAction } from './evidenceUtils';
import tableStyles from '../RoadmapReportsPage.module.css';
import styles from './RoadmapV2.module.css';

export interface ActionModalProps {
  initiative: InitiativeState;
  action: InitiativeAction;
  teamColors: TeamColors;
  /** Open the whole initiative (its Dates and sub-actions section) in place. */
  onOpenInitiative: () => void;
  /** Open a sibling sub-action (named under Waiting on / Holds up) in place. */
  onOpenAction?: (actionKey: string) => void;
  onClose: () => void;
  /** The source ledger, so a cited meeting reads as its name instead of its id. */
  sourceLedger?: readonly EvidenceSource[];
  /** Opens the source viewer at a quote. */
  onOpenSource?: OpenSourceFn;
  /** ISO day; injectable clock for tests. */
  today: string;
}

function day(iso: string | null): string | null {
  const d = parseDay(iso);
  return d ? fmtDayYear(d) : null;
}

export function ActionModal({
  initiative: i,
  action: a,
  teamColors,
  onOpenInitiative,
  onOpenAction,
  onClose,
  sourceLedger = [],
  onOpenSource,
  today,
}: ActionModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const siblings = useMemo(() => liveActions(i.actions), [i.actions]);
  const blockers = actionBlockerRefs(a, siblings);
  const holdsUp = actionHoldsUpRefs(a, siblings);
  // A sibling reads as its title; with onOpenAction it is a button to that sibling's pop-up.
  const sibling = (r: ActionRef) => (onOpenAction ? (
    <button
      type="button"
      className={styles.actionLink}
      onClick={() => onOpenAction(r.key)}
      title="Open this sub-action"
      data-action="roadmap_action_open"
      data-target-initiative-key={i.initiative_key}
      data-target-action-key={r.key}
    >
      {r.title}
    </button>
  ) : r.title);
  const { end, endKind } = actionDateSpan(a);
  const late = actionIsLate(a, today);
  const quotes = useMemo(() => evidenceForAction(i.evidence, a.key), [i.evidence, a.key]);
  const laneColor = teamColors[accentTeamOf(i) ?? ''] ?? '#94a3b8';

  // Escape closes; Tab stays inside the dialog (focus trap), as in InitiativeModal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { dialogRef.current?.focus(); }, [i.initiative_key, a.key]);

  const started = day(a.started_on);
  const ended = day(end);

  return (
    <>
      <div className={styles.modalBackdrop} onClick={onClose} data-testid="modal-backdrop" />
      <div
        ref={dialogRef}
        className={`${styles.modal} ${styles.actionModal}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Sub-action: ${a.title}`}
        tabIndex={-1}
        data-testid="action-modal"
        data-initiative-key={i.initiative_key}
        data-action-key={a.key}
      >
        <header className={styles.modalHead}>
          <div className={styles.modalHeadMain}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={onOpenInitiative}
              data-action="roadmap_action_open_initiative"
              data-target-initiative-key={i.initiative_key}
              data-testid="action-modal-parent"
              title="Open the whole initiative"
            >
              <span className={styles.actionParentDot} style={{ background: laneColor }} aria-hidden="true" />
              Part of {i.title}
              <span className={`${tableStyles.statusChip} ${tableStyles[`status_${i.status}`] ?? ''}`} style={{ marginLeft: 6 }}>{STATUS_LABELS[i.status]}</span>
            </button>
            <h2>{a.title}</h2>
            <div className={styles.chips}>
              <span className={`${styles.actionStatus} ${styles[`action_${a.status}`] ?? ''}`}>{ACTION_STATUS_LABELS[a.status] ?? a.status}</span>
              {late && <span className={`${styles.actionStatus} ${styles.actionLateChip}`} data-testid="action-late">Late</span>}
              <span className={styles.meta}>
                {a.people.length > 0 ? a.people.join(', ') : 'nobody named'}
              </span>
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} data-action="roadmap_action_close">Close</button>
        </header>

        <div className={styles.modalBody}>
          <section className={styles.section} aria-label="Dates">
            <h3>Dates</h3>
            <dl className={styles.kv}>
              <dt>Started</dt>
              <dd>{started ?? <span className={styles.muted}>not stated</span>}</dd>
              <dt>{endKind === 'completed' ? 'Completed' : 'Target'}</dt>
              <dd>
                {ended
                  ? <span className={late ? styles.actionLate : undefined}>{ended}{late ? ' (passed)' : ''}</span>
                  : a.status === 'done' || a.status === 'dropped'
                    ? <span className={styles.muted}>date not stated</span>
                    : <span className={styles.needsTarget}>no stated target</span>}
              </dd>
            </dl>
          </section>

          <section className={styles.section} aria-label="Waiting on">
            <h3>Waiting on</h3>
            {blockers.length === 0
              ? <div className={styles.muted}>Nothing in this initiative has to finish first.</div>
              : <ul className={styles.plainList} aria-label="Waits on">{blockers.map((r) => <li key={r.key}>{sibling(r)}</li>)}</ul>}
          </section>

          <section className={styles.section} aria-label="Holds up">
            <h3>Holds up</h3>
            {holdsUp.length === 0
              ? <div className={styles.muted}>No other sub-action waits on this one.</div>
              : <ul className={styles.plainList} aria-label="Held up">{holdsUp.map((r) => <li key={r.key}>{sibling(r)}</li>)}</ul>}
          </section>

          <section className={styles.section} aria-label="Note">
            <h3>Merlin's note</h3>
            {a.note ? <p className={styles.summaryText}>{a.note}</p> : <div className={styles.muted}>No note recorded.</div>}
          </section>

          <section className={styles.section} aria-label="Evidence">
            <h3>Evidence</h3>
            <EvidenceList
              evidence={quotes}
              onOpenSource={onOpenSource}
              aria-label="Quotes behind this sub-action"
              emptyNote="No quote recorded for this sub-action yet."
            />
            {a.sources.length > 0 && (
              <>
                <div className={styles.evidenceSub}>Cited sources</div>
                <CitedSources sources={a.sources} ledger={sourceLedger} onOpenSource={onOpenSource} plain />
              </>
            )}
          </section>

          <section className={`${styles.section} ${styles.actionModalFoot}`} aria-label="Initiative">
            <button
              type="button"
              className={styles.linkBtn}
              onClick={onOpenInitiative}
              data-action="roadmap_action_open_initiative"
              data-target-initiative-key={i.initiative_key}
              data-testid="action-modal-open-initiative"
            >
              Open the whole initiative: {i.title} ({siblings.length} sub-action{siblings.length === 1 ? '' : 's'})
            </button>
          </section>
        </div>
      </div>
    </>
  );
}
